import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Alert,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Payment as PaymentIcon,
  AccountBalance as AccountBalanceIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PictureAsPdf as PdfIcon,
  BarChart as BarChartIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { saleService, Sale } from '../services/saleService';
import { utilityService } from '../services/utilityService';
import { ledgerService, MonthlyReport, ClientBalance, LedgerEntry } from '../services/ledgerService';
import { clientService } from '../services/clientService';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Extend jsPDF type to include autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

// Add autoTable to jsPDF prototype
(jsPDF as any).prototype.autoTable = autoTable;

interface ReportData {
  id: string;
  clientName: string;
  date: string;
  quantity: number;
  price: number;
  total: number;
}

interface LedgerReportData {
  month: string;
  totalSales: number;
  totalPayments: number;
  totalAdvances: number;
  netAmount: number;
  pendingPayments: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tabpanel-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Reports: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState<string>(
    new Date().toISOString().slice(0, 7)
  );
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [allData, setAllData] = useState<ReportData[]>([]);
  const [filteredData, setFilteredData] = useState<ReportData[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noRecords, setNoRecords] = useState(false);
  const [ledgerReport, setLedgerReport] = useState<LedgerReportData | null>(null);
  const [pendingPayments, setPendingPayments] = useState<ClientBalance[]>([]);
  const [outstandingAdvances, setOutstandingAdvances] = useState<ClientBalance[]>([]);
  
  // Ledger state
  const [ledgerEntries, setLedgerEntries] = useState<LedgerEntry[]>([]);
  const [clientBalances, setClientBalances] = useState<ClientBalance[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState<LedgerEntry | null>(null);
  const [includeSummary, setIncludeSummary] = useState(true);
  const [formData, setFormData] = useState({
    clientId: '',
    type: 'payment' as 'payment' | 'advance',
    amount: '',
    description: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    reference: ''
  });

  const { currentUser, userProfile, userPermissions, selectedAgent } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    console.log('Reports component mounted, currentUser:', currentUser);
    loadData();
  }, [currentUser, navigate]);

  // Debug effect to see when clients state changes
  useEffect(() => {
    console.log('Clients state changed:', clients);
    console.log('Clients length:', clients.length);
    if (clients.length > 0) {
      console.log('First client:', clients[0]);
      console.log('Client ID:', clients[0].id);
      console.log('Client name:', clients[0].name);
    }
  }, [clients]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load basic data first
      const [sales, pendingPaymentsData, outstandingAdvancesData, entries, balances] = await Promise.all([
        saleService.getSales(),
        ledgerService.getPendingPayments(),
        ledgerService.getOutstandingAdvances(),
        ledgerService.getLedgerEntries(),
        ledgerService.getAllClientBalances()
      ]);
      
      console.log('Sales data:', sales);
      
      // Load clients separately to catch any specific errors
      let clientsData: any[] = [];
      try {
        if (!userProfile) {
          setError('User profile not available');
          clientsData = [];
        } else {
          clientsData = await clientService.getClients(userProfile.role, selectedAgent || undefined);
        }
        console.log('Clients data from service:', clientsData);
      } catch (clientError) {
        console.error('Error loading clients:', clientError);
        setError('Failed to load clients. Please check your permissions.');
        clientsData = [];
      }

      // Set clients data from clientService
      setClients(clientsData);
      console.log('Clients loaded:', clientsData);
      console.log('First client object:', clientsData[0]);
      console.log('Clients array length:', clientsData.length);

      // Transform sales data to report format
      const transformedData = sales.map(sale => ({
        id: sale.id!,
        clientName: sale.clientName,
        date: sale.date,
        quantity: sale.quantity,
        price: sale.pricePerLiter,
        total: sale.total
      }));

      setAllData(transformedData);
      setPendingPayments(pendingPaymentsData);
      setOutstandingAdvances(outstandingAdvancesData);
      setLedgerEntries(entries);
      setClientBalances(balances);
      
      // Filter data for current month by default
      const currentMonth = new Date().toISOString().slice(0, 7);
      const [year, month] = currentMonth.split('-');
      const startOfMonth = new Date(parseInt(year), parseInt(month) - 1, 1);
      const endOfMonth = new Date(parseInt(year), parseInt(month), 0, 23, 59, 59, 999);

      const filtered = transformedData.filter(sale => {
        const saleDate = new Date(sale.date);
        return saleDate >= startOfMonth && saleDate <= endOfMonth;
      });

      setFilteredData(filtered);
      setNoRecords(filtered.length === 0);

      // Generate ledger report for current month
      await generateLedgerReport(currentMonth);
    } catch (err) {
      console.error('Error loading data:', err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to load data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const generateLedgerReport = async (month: string) => {
    try {
      const [year, monthNum] = month.split('-');
      const report = await ledgerService.generateMonthlyReport(month, parseInt(year));
      
      setLedgerReport({
        month,
        totalSales: report.totalSales,
        totalPayments: report.totalPayments,
        totalAdvances: report.totalAdvances,
        netAmount: report.netAmount,
        pendingPayments: report.pendingPayments
      });
    } catch (err) {
      console.error('Error generating ledger report:', err);
    }
  };

  // Ledger functions
  const handleSubmit = async () => {
    try {
      if (!formData.clientId || !formData.amount) {
        setError('Please fill in all required fields');
        return;
      }

      const client = clients.find(c => c.id === formData.clientId);
      if (!client) {
        setError('Client not found');
        return;
      }

      const entryData: {
        clientId: string;
        clientName: string;
        type: 'payment' | 'advance';
        amount: number;
        description: string;
        date: string;
        reference: string;
      } = {
        clientId: formData.clientId,
        clientName: client.name,
        type: formData.type,
        amount: parseFloat(formData.amount),
        description: formData.description || '',
        date: formData.date,
        reference: formData.reference || ''
      };

      if (editingEntry) {
        await ledgerService.updateLedgerEntry(editingEntry.id!, entryData);
        console.log('Entry updated successfully');
      } else {
        await ledgerService.addLedgerEntry(entryData);
        console.log('Entry added successfully');
      }

      setOpenDialog(false);
      setEditingEntry(null);
      resetForm();
      loadData();
      setError(null);
    } catch (err) {
      console.error('Error in handleSubmit:', err);
      setError(err instanceof Error ? err.message : 'Failed to save entry');
    }
  };

  const handleEdit = (entry: LedgerEntry) => {
    setEditingEntry(entry);
    setFormData({
      clientId: entry.clientId,
      type: entry.type,
      amount: entry.amount.toString(),
      description: entry.description || '',
      date: format(new Date(entry.date), 'yyyy-MM-dd'),
      reference: entry.reference || ''
    });
    setOpenDialog(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      try {
        await ledgerService.deleteLedgerEntry(id);
        loadData();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete entry');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      clientId: '',
      type: 'payment',
      amount: '',
      description: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      reference: ''
    });
  };

  const openNewDialog = () => {
    setEditingEntry(null);
    resetForm();
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setEditingEntry(null);
    resetForm();
    setError(null);
  };

  // Balance interpretation functions
  const getBalanceColor = (balance: number) => {
    if (balance > 0) return 'error';
    if (balance < 0) return 'success';
    return 'default';
  };

  const getBalanceText = (balance: number) => {
    if (balance > 0) return `+${balance.toFixed(2)}`;
    if (balance < 0) return `-${Math.abs(balance).toFixed(2)}`;
    return '0.00';
  };

  const exportToPDF = () => {
    try {
      if (!ledgerReport || !ledgerEntries.length) {
        setError('No data available for export');
        return;
      }

      const doc = new jsPDF();
      
      // Title
      doc.setFontSize(20);
      doc.text('Ledger Report', 105, 20, { align: 'center' });
      
      // Date
      doc.setFontSize(12);
      doc.text(`Generated on: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, 20, 35);
      
      let yPosition = 50;
      
      // Monthly Summary if enabled
      if (includeSummary && ledgerReport) {
        doc.setFontSize(16);
        doc.text(`Monthly Summary - ${ledgerReport.month}`, 20, yPosition);
        yPosition += 15;
        
        doc.setFontSize(12);
        doc.text(`Total Sales: KES ${ledgerReport.totalSales.toLocaleString()}`, 20, yPosition);
        yPosition += 10;
        doc.text(`Total Payments: KES ${ledgerReport.totalPayments.toLocaleString()}`, 20, yPosition);
        yPosition += 10;
        doc.text(`Total Advances: KES ${ledgerReport.totalAdvances.toLocaleString()}`, 20, yPosition);
        yPosition += 10;
        doc.text(`Net Amount: KES ${ledgerReport.netAmount.toLocaleString()}`, 20, yPosition);
        yPosition += 10;
        doc.text(`Pending Payments: KES ${ledgerReport.pendingPayments.toLocaleString()}`, 20, yPosition);
        yPosition += 20;
      }
      
      // Client Balances
      if (clientBalances.length > 0) {
        doc.setFontSize(16);
        doc.text('Client Balances', 20, yPosition);
        yPosition += 15;
        
        const balanceData = clientBalances.map(balance => [
          balance.clientName,
          `KES ${balance.totalSales.toLocaleString()}`,
          `KES ${balance.totalPayments.toLocaleString()}`,
          `KES ${balance.totalAdvances.toLocaleString()}`,
          `KES ${balance.balance.toLocaleString()}`
        ]);
        
        doc.autoTable({
          startY: yPosition,
          head: [['Client', 'Sales', 'Payments', 'Advances', 'Balance']],
          body: balanceData,
          theme: 'grid',
          headStyles: { fillColor: [66, 66, 66] },
          styles: { fontSize: 10 }
        });
        
        yPosition = (doc as any).lastAutoTable.finalY + 20;
      }
      
      // Ledger Entries
      doc.setFontSize(16);
      doc.text('Ledger Entries', 20, yPosition);
      yPosition += 15;
      
      const entryData = ledgerEntries.map(entry => [
        format(new Date(entry.date), 'dd/MM/yyyy'),
        entry.clientName,
        entry.type === 'payment' ? 'Payment' : 'Advance',
        entry.description || '-',
        `KES ${entry.amount.toLocaleString()}`,
        entry.reference || '-'
      ]);
      
      doc.autoTable({
        startY: yPosition,
        head: [['Date', 'Client', 'Type', 'Description', 'Amount', 'Reference']],
        body: entryData,
        theme: 'grid',
        headStyles: { fillColor: [66, 66, 66] },
        styles: { fontSize: 8 }
      });
      
      // Save the PDF
      const filename = `ledger-report-${selectedMonth}-${format(new Date(), 'dd-MM-yyyy')}.pdf`;
      doc.save(filename);
      
      // Clear any previous errors
      setError(null);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      setError('Failed to export PDF. Please try again.');
    }
  };

  const handleGenerate = () => {
    let filtered = [...allData];

    // Apply month filter if selected
    if (selectedMonth) {
      const [year, month] = selectedMonth.split('-');
      const startOfMonth = new Date(parseInt(year), parseInt(month) - 1, 1);
      const endOfMonth = new Date(parseInt(year), parseInt(month), 0, 23, 59, 59, 999);

      filtered = filtered.filter(sale => {
        const saleDate = new Date(sale.date);
        return saleDate >= startOfMonth && saleDate <= endOfMonth;
      });
    }

    // Apply date range filter if both dates are provided
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      filtered = filtered.filter(sale => {
        const saleDate = new Date(sale.date);
        return saleDate >= start && saleDate <= end;
      });
    }

    // Apply client filter if selected
    if (selectedClient) {
      const selectedClientObj = clients.find(c => c.id === selectedClient);
      if (selectedClientObj) {
        filtered = filtered.filter(sale => sale.clientName === selectedClientObj.name);
      }
    }

    setNoRecords(filtered.length === 0);
    setFilteredData(filtered);
  };

  const handleExport = () => {
    const data = filteredData.map(row => ({
      'Client Name': row.clientName,
      'Date': row.date,
      'Quantity (L)': row.quantity.toFixed(2),
      'Price (KES)': `KES ${row.price.toLocaleString()}`,
      'Total (KES)': `KES ${row.total.toLocaleString()}`
    }));
    
    let filename = 'milk-collection-report';
    if (selectedMonth) {
      filename += `-${selectedMonth}`;
    } else if (startDate && endDate) {
      filename += `-${startDate}-to-${endDate}`;
    } else {
      filename += '-all-time';
    }
    
    utilityService.exportToCSV(data, `${filename}.csv`);
  };

  const handleClientChange = (e: any) => {
    setSelectedClient(e.target.value);
    // Reset other filters when client changes
    setSelectedMonth('');
    setStartDate('');
    setEndDate('');
  };

  if (!currentUser) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Reports & Ledger
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Month Selection for Ledger Summary */}
        {userPermissions?.canManageLedger && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Select Month for Ledger Summary
            </Typography>
            <TextField
              label="Month"
              type="month"
              value={selectedMonth}
              onChange={async (e) => {
                const month = e.target.value;
                setSelectedMonth(month);
                if (month) {
                  await generateLedgerReport(month);
                }
              }}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ minWidth: 200 }}
            />
          </Box>
        )}

        {/* Monthly Summary Cards */}
        {userPermissions?.canManageLedger && ledgerReport && (
          <Card sx={{ mb: 3 }}>
            <CardHeader
              title={`Monthly Ledger Summary - ${ledgerReport.month}`}
              avatar={<AccountBalanceIcon />}
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={2}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light', color: 'white' }}>
                    <Typography variant="h6">Total Sales</Typography>
                    <Typography variant="h4">KES {ledgerReport.totalSales.toLocaleString()}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light', color: 'white' }}>
                    <Typography variant="h6">Total Payments</Typography>
                    <Typography variant="h4">KES {ledgerReport.totalPayments.toLocaleString()}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light', color: 'white' }}>
                    <Typography variant="h6">Total Advances</Typography>
                    <Typography variant="h4">KES {ledgerReport.totalAdvances.toLocaleString()}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light', color: 'white' }}>
                    <Typography variant="h6">Net Amount</Typography>
                    <Typography variant="h4">KES {ledgerReport.netAmount.toLocaleString()}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light', color: 'white' }}>
                    <Typography variant="h6">Pending Payments</Typography>
                    <Typography variant="h4">KES {ledgerReport.pendingPayments.toLocaleString()}</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="Sales Reports" icon={<BarChartIcon />} iconPosition="start" />
            {userPermissions?.canManageLedger && (
              <Tab label="Ledger Management" icon={<AccountBalanceIcon />} iconPosition="start" />
            )}
            {userPermissions?.canManageLedger && (
              <Tab label="Financial Status" icon={<PeopleIcon />} iconPosition="start" />
            )}
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          {/* Sales Reports Tab */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Client</InputLabel>
                <Select
                  value={selectedClient}
                  label="Client"
                  onChange={handleClientChange}
                >
                  <MenuItem value="">All Clients</MenuItem>
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Month"
                type="month"
                value={selectedMonth}
                onChange={async (e) => {
                  const month = e.target.value;
                  setSelectedMonth(month);
                  if (month && userPermissions?.canManageLedger) {
                    await generateLedgerReport(month);
                  }
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleGenerate}
                >
                  Generate Report
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleExport}
                  disabled={filteredData.length === 0}
                >
                  Export Report
                </Button>
              </Box>
            </Grid>
          </Grid>

          {loading ? (
            <Typography>Loading data...</Typography>
          ) : noRecords ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              No records available for the selected period
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Client Name</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Quantity (L)</TableCell>
                    <TableCell align="right">Price (KES)</TableCell>
                    <TableCell align="right">Total (KES)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.clientName}</TableCell>
                      <TableCell>{utilityService.formatDate(row.date)}</TableCell>
                      <TableCell align="right">{row.quantity.toFixed(2)}</TableCell>
                      <TableCell align="right">{row.price.toLocaleString()}</TableCell>
                      <TableCell align="right">{row.total.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {userPermissions?.canManageLedger && (
          <TabPanel value={tabValue} index={1}>
            {/* Ledger Management Tab */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Ledger Management
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<PdfIcon />}
                  onClick={exportToPDF}
                  disabled={!ledgerReport || !ledgerEntries.length}
                  color="secondary"
                >
                  Export PDF
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openNewDialog}
                >
                  Add Entry
                </Button>
              </Box>
            </Box>

            {/* Client Balances Summary */}
            <Card sx={{ mb: 3 }}>
              <CardHeader
                title="Client Balances Summary"
                avatar={<PeopleIcon />}
              />
              <CardContent>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Client Name</TableCell>
                        <TableCell align="right">Total Sales</TableCell>
                        <TableCell align="right">Total Payments</TableCell>
                        <TableCell align="right">Total Advances</TableCell>
                        <TableCell align="right">Balance</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clientBalances.map((balance) => (
                        <TableRow key={balance.clientId}>
                          <TableCell>{balance.clientName}</TableCell>
                          <TableCell align="right">{balance.totalSales.toFixed(2)}</TableCell>
                          <TableCell align="right">{balance.totalPayments.toFixed(2)}</TableCell>
                          <TableCell align="right">{balance.totalAdvances.toFixed(2)}</TableCell>
                          <TableCell align="right">
                            <Chip
                              label={getBalanceText(balance.balance)}
                              color={getBalanceColor(balance.balance)}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            {/* Ledger Entries */}
            <Card>
              <CardHeader
                title="Ledger Entries"
                avatar={<PaymentIcon />}
              />
              <CardContent>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Reference</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ledgerEntries.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell>{format(new Date(entry.date), 'dd/MM/yyyy')}</TableCell>
                          <TableCell>{entry.clientName}</TableCell>
                          <TableCell>
                            <Chip
                              label={entry.type === 'payment' ? 'Payment' : 'Advance'}
                              color={entry.type === 'payment' ? 'success' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{entry.description || '-'}</TableCell>
                          <TableCell align="right">{entry.amount.toFixed(2)}</TableCell>
                          <TableCell>{entry.reference || '-'}</TableCell>
                          <TableCell>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleEdit(entry)}>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => handleDelete(entry.id!)}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </TabPanel>
        )}

        {userPermissions?.canManageLedger && (
          <TabPanel value={tabValue} index={2}>
            {/* Financial Status Tab */}
            <Typography variant="h6" gutterBottom>
              Financial Status Overview
            </Typography>
            
            {/* Pending Payments and Outstanding Advances */}
            {(pendingPayments.length > 0 || outstandingAdvances.length > 0) && (
              <Grid container spacing={2}>
                {pendingPayments.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, bgcolor: 'error.light', color: 'white' }}>
                      <Typography variant="h6" gutterBottom>
                        Pending Payments ({pendingPayments.length} clients)
                      </Typography>
                      <Typography variant="body2">
                        Total Amount Owed: KES {pendingPayments.reduce((sum, p) => sum + p.balance, 0).toLocaleString()}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                {outstandingAdvances.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, bgcolor: 'success.light', color: 'white' }}>
                      <Typography variant="h6" gutterBottom>
                        Outstanding Advances ({outstandingAdvances.length} clients)
                      </Typography>
                      <Typography variant="body2">
                        Total Amount: KES {outstandingAdvances.reduce((sum, a) => sum + Math.abs(a.balance), 0).toLocaleString()}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            )}
          </TabPanel>
        )}
      </Paper>

      {/* Add/Edit Ledger Entry Dialog */}
      {userPermissions?.canManageLedger && (
        <Dialog open={openDialog} onClose={closeDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingEntry ? 'Edit Ledger Entry' : 'Add New Ledger Entry'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Client</InputLabel>
                <Select
                  value={formData.clientId}
                  onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                  label="Client"
                >
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as 'payment' | 'advance' })}
                  label="Type"
                >
                  <MenuItem value="payment">Payment</MenuItem>
                  <MenuItem value="advance">Advance</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Amount"
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                fullWidth
                inputProps={{ step: "0.01", min: "0" }}
              />

              <TextField
                label="Description (Optional)"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />

              <TextField
                label="Date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Reference (Optional)"
                value={formData.reference}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                fullWidth
                placeholder="Payment method, check number, etc."
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained">
              {editingEntry ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
};

export default Reports; 